import React, { useEffect, useRef, useState, useCallback } from "react";
import { adminService } from "./adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
	Trash2,
	Plus,
	Phone,
	RefreshCw,
	Pencil,
	Check,
	X,
	Upload,
	Store,
	UtensilsCrossed,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	Search,
} from "lucide-react";

interface PreApprovedEntry {
	id: number;
	restaurant_id: number | null;
	phone_number: string;
	restaurant_name: string | null;
	created_at: string;
}

// ─── Row-level edit state ────────────────────────────────────────────────────
interface EditState {
	restaurant_id: string; // keep as string while editing
	phone_number: string;
	restaurant_name: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const ManagePreApproved: React.FC = () => {
	const { toast } = useToast();
	const fileInputRef = useRef<HTMLInputElement>(null);

	// ── List + pagination state ───────────────────────────────────────────────
	const [entries, setEntries] = useState<PreApprovedEntry[]>([]);
	const [total, setTotal] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [loading, setLoading] = useState(false);

	// ── Add-form state ────────────────────────────────────────────────────────
	const [addRows, setAddRows] = useState<{ restaurant_id: string; phone: string; restaurant_name: string }[]>([
		{ restaurant_id: "", phone: "", restaurant_name: "" },
	]);
	const [adding, setAdding] = useState(false);
	const [csvUploading, setCsvUploading] = useState(false);

	// ── Inline-edit state ─────────────────────────────────────────────────────
	const [editingId, setEditingId] = useState<number | null>(null);
	const [editState, setEditState] = useState<EditState>({ restaurant_id: "", phone_number: "", restaurant_name: "" });
	const [saving, setSaving] = useState(false);

	// ── Delete state ──────────────────────────────────────────────────────────
	const [deletingId, setDeletingId] = useState<number | null>(null);

	// ─────────────────────────────────────────────────────────────────────────
	// Fetch
	// ─────────────────────────────────────────────────────────────────────────
	const fetchEntries = useCallback(async (p = page, ps = pageSize, s = search) => {
		setLoading(true);
		try {
			const res = await adminService.listPreApproved({ page: p, page_size: ps, search: s || undefined });
			const data = res.data;
			setEntries(data.results);
			setTotal(data.total);
			setTotalPages(data.total_pages);
			setPage(data.page);
		} catch {
			toast({ title: "Failed to load pre-approved entries", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	}, [page, pageSize, search]);

	useEffect(() => {
		fetchEntries(page, pageSize, search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, pageSize, search]);

	// ─────────────────────────────────────────────────────────────────────────
	// Search
	// ─────────────────────────────────────────────────────────────────────────
	const handleSearchSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setSearch(searchInput.trim());
		setPage(1);
	};

	const handleSearchClear = () => {
		setSearchInput("");
		setSearch("");
		setPage(1);
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Pagination helpers
	// ─────────────────────────────────────────────────────────────────────────
	const goToPage = (p: number) => {
		const clamped = Math.max(1, Math.min(p, totalPages));
		setPage(clamped);
		setEditingId(null);
	};

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPageSize(Number(e.target.value));
		setPage(1);
		setEditingId(null);
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Add — manual rows
	// ─────────────────────────────────────────────────────────────────────────
	const handleAddRow = () =>
		setAddRows((prev) => [...prev, { restaurant_id: "", phone: "", restaurant_name: "" }]);

	const handleRemoveAddRow = (idx: number) =>
		setAddRows((prev) => prev.filter((_, i) => i !== idx));

	const handleAddRowChange = (
		idx: number,
		field: "restaurant_id" | "phone" | "restaurant_name",
		value: string,
	) =>
		setAddRows((prev) =>
			prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
		);

	const handleAdd = async () => {
		const entries_payload = addRows
			.filter((r) => r.phone.trim())
			.map((r) => ({
				phone_number: r.phone.trim(),
				restaurant_id: r.restaurant_id.trim()
					? parseInt(r.restaurant_id.trim(), 10) || null
					: null,
				restaurant_name: r.restaurant_name.trim() || null,
			}));

		if (entries_payload.length === 0) return;
		setAdding(true);
		try {
			const res = await adminService.addPreApproved(entries_payload);
			const { added, skipped } = res.data;
			if (added.length > 0) {
				toast({
					title: `Added ${added.length} entr${added.length === 1 ? "y" : "ies"}`,
					description: added
						.map((a: any) => {
							const parts = [a.phone_number];
							if (a.restaurant_id) parts.push(`rest. ${a.restaurant_id}`);
							if (a.restaurant_name) parts.push(a.restaurant_name);
							return parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(", ")})` : parts[0];
						})
						.join(", "),
				});
			}
			if (skipped.length > 0) {
				toast({
					title: `Skipped ${skipped.length} entr${skipped.length === 1 ? "y" : "ies"}`,
					description: skipped
						.map((s: any) => `${s.phone_number}: ${s.reason}`)
						.join(" | "),
					variant: "destructive",
				});
			}
			setAddRows([{ restaurant_id: "", phone: "", restaurant_name: "" }]);
			// Go to page 1 to see newly added entries (sorted by -created_at)
			setPage(1);
			fetchEntries(1, pageSize, search);
		} catch {
			toast({ title: "Failed to add entries", variant: "destructive" });
		} finally {
			setAdding(false);
		}
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Add — CSV upload
	// ─────────────────────────────────────────────────────────────────────────
	const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		// Reset so the same file can be re-selected after a fix
		e.target.value = "";
		setCsvUploading(true);
		try {
			const res = await adminService.addPreApprovedCsv(file);
			const { added, skipped } = res.data;
			if (added.length > 0) {
				toast({
					title: `CSV: added ${added.length} entr${added.length === 1 ? "y" : "ies"}`,
					description: added
						.map((a: any) => {
							const parts = [a.phone_number];
							if (a.restaurant_id) parts.push(`rest. ${a.restaurant_id}`);
							if (a.restaurant_name) parts.push(a.restaurant_name);
							return parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(", ")})` : parts[0];
						})
						.join(", "),
				});
			}
			if (skipped.length > 0) {
				toast({
					title: `CSV: skipped ${skipped.length}`,
					description: skipped
						.map((s: any) => `${s.phone_number}: ${s.reason}`)
						.join(" | "),
					variant: "destructive",
				});
			}
			setPage(1);
			fetchEntries(1, pageSize, search);
		} catch {
			toast({ title: "CSV upload failed", variant: "destructive" });
		} finally {
			setCsvUploading(false);
		}
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Inline edit
	// ─────────────────────────────────────────────────────────────────────────
	const startEdit = (entry: PreApprovedEntry) => {
		setEditingId(entry.id);
		setEditState({
			restaurant_id: entry.restaurant_id != null ? String(entry.restaurant_id) : "",
			phone_number: entry.phone_number,
			restaurant_name: entry.restaurant_name ?? "",
		});
	};

	const cancelEdit = () => {
		setEditingId(null);
	};

	const handleSaveEdit = async (id: number) => {
		setSaving(true);
		try {
			const payload: { restaurant_id?: number | null; phone_number?: string; restaurant_name?: string | null } = {
				restaurant_id: editState.restaurant_id.trim()
					? parseInt(editState.restaurant_id.trim(), 10) || null
					: null,
				phone_number: editState.phone_number.trim(),
				restaurant_name: editState.restaurant_name.trim() || null,
			};
			const res = await adminService.editPreApproved(id, payload);
			setEntries((prev) =>
				prev.map((e) =>
					e.id === id
						? {
								...e,
								restaurant_id: res.data.restaurant_id,
								phone_number: res.data.phone_number,
								restaurant_name: res.data.restaurant_name,
						  }
						: e,
				),
			);
			toast({ title: "Entry updated" });
			setEditingId(null);
		} catch (err: any) {
			const msg =
				err?.response?.data?.errors
					? Object.values(err.response.data.errors).join(" | ")
					: "Failed to save";
			toast({ title: msg, variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Delete
	// ─────────────────────────────────────────────────────────────────────────
	const handleDelete = async (entry: PreApprovedEntry) => {
		const label = [
			entry.restaurant_id ? `rest. ${entry.restaurant_id}` : null,
			entry.phone_number,
			entry.restaurant_name,
		].filter(Boolean).join(" · ");
		if (!window.confirm(`Remove ${label} from pre-approved list?`)) return;
		setDeletingId(entry.id);
		try {
			const res = await adminService.deletePreApproved(entry.id);
			const { phone_number, restaurant_id } = res.data;
			const deletedLabel = restaurant_id
				? `${phone_number} (rest. ${restaurant_id})`
				: phone_number;
			toast({ title: `Removed ${deletedLabel}` });
			// If we deleted the last item on a non-first page, go back one page
			const newTotal = total - 1;
			const newTotalPages = Math.max(1, Math.ceil(newTotal / pageSize));
			const newPage = Math.min(page, newTotalPages);
			if (newPage !== page) {
				setPage(newPage);
				fetchEntries(newPage, pageSize, search);
			} else {
				fetchEntries(page, pageSize, search);
			}
		} catch {
			toast({ title: "Failed to delete", variant: "destructive" });
		} finally {
			setDeletingId(null);
		}
	};

	// ─────────────────────────────────────────────────────────────────────────
	// Render
	// ─────────────────────────────────────────────────────────────────────────
	const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
	const endRow = Math.min(page * pageSize, total);

	return (
		<div className="max-w-5xl w-full space-y-6">

			{/* ── Add Entries ─────────────────────────────────────────────────── */}
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
				<div>
					<h3 className="text-lg font-bold text-gray-900 mb-1">Add Pre-Approved Entries</h3>
					<p className="text-sm text-gray-500">
						Enter Petpooja restaurant ID, 10-digit phone number, and optional restaurant name, or upload a CSV.
					</p>
				</div>

				{/* Column headers for add rows */}
				<div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
					<span>Restaurant ID</span>
					<span>Phone Number</span>
					<span>Restaurant Name</span>
					<span />
				</div>

				{/* Manual rows */}
				<div className="space-y-2">
					{addRows.map((row, idx) => (
						<div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
							<div className="flex items-center gap-1.5">
								<Store className="h-4 w-4 text-gray-400 shrink-0" />
								<Input
									placeholder="Restaurant ID"
									value={row.restaurant_id}
									onChange={(e) => handleAddRowChange(idx, "restaurant_id", e.target.value)}
									className="font-mono text-sm"
									type="number"
								/>
							</div>
							<div className="flex items-center gap-1.5">
								<Phone className="h-4 w-4 text-gray-400 shrink-0" />
								<Input
									placeholder="Phone (10 digits)"
									value={row.phone}
									onChange={(e) => handleAddRowChange(idx, "phone", e.target.value)}
									className="font-mono text-sm"
									maxLength={10}
								/>
							</div>
							<div className="flex items-center gap-1.5">
								<UtensilsCrossed className="h-4 w-4 text-gray-400 shrink-0" />
								<Input
									placeholder="Restaurant name (optional)"
									value={row.restaurant_name}
									onChange={(e) => handleAddRowChange(idx, "restaurant_name", e.target.value)}
									className="text-sm"
								/>
							</div>
							{addRows.length > 1 ? (
								<Button
									variant="ghost"
									size="icon"
									onClick={() => handleRemoveAddRow(idx)}
									className="text-gray-400 hover:text-red-500 shrink-0"
								>
									<X className="h-4 w-4" />
								</Button>
							) : (
								<span className="w-8" />
							)}
						</div>
					))}
				</div>

				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={handleAddRow}
						className="gap-1.5 text-gray-600"
					>
						<Plus className="h-3.5 w-3.5" />
						Add row
					</Button>
					<Button
						onClick={handleAdd}
						disabled={adding || addRows.every((r) => !r.phone.trim())}
						className="bg-intellend-green hover:bg-intellend-green/90 text-white gap-2"
					>
						<Plus className="h-4 w-4" />
						{adding ? "Adding…" : "Save Entries"}
					</Button>

					{/* CSV upload */}
					<input
						ref={fileInputRef}
						type="file"
						accept=".csv,text/csv"
						className="hidden"
						onChange={handleCsvUpload}
					/>
					<Button
						variant="outline"
						onClick={() => fileInputRef.current?.click()}
						disabled={csvUploading}
						className="gap-2 text-gray-600"
					>
						<Upload className="h-4 w-4" />
						{csvUploading ? "Uploading…" : "Upload CSV"}
					</Button>
				</div>

				<p className="text-xs text-gray-400">
					CSV format:{" "}
					<code className="bg-gray-100 px-1 rounded">restaurant_id,phone_number,restaurant_name</code>{" "}
					(header required; <code className="bg-gray-100 px-1 rounded">restaurant_id</code> and{" "}
					<code className="bg-gray-100 px-1 rounded">restaurant_name</code> columns are optional).
				</p>
			</div>

			{/* ── Current List ─────────────────────────────────────────────────── */}
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
				{/* Header row */}
				<div className="flex flex-wrap items-center justify-between gap-3 mb-4">
					<div>
						<h3 className="text-lg font-bold text-gray-900">Current Pre-Approved Entries</h3>
						<p className="text-sm text-gray-500">
							{total} entr{total !== 1 ? "ies" : "y"} on file
							{search && (
								<span className="ml-1 text-intellend-green font-medium">
									(filtered)
								</span>
							)}
						</p>
					</div>
					<div className="flex items-center gap-2">
						{/* Search */}
						<form onSubmit={handleSearchSubmit} className="flex items-center gap-1">
							<div className="relative">
								<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
								<Input
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									placeholder="Search phone / name…"
									className="pl-8 h-8 text-sm w-48"
								/>
							</div>
							{searchInput && (
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={handleSearchClear}
									className="h-8 w-8 text-gray-400 hover:text-gray-600"
								>
									<X className="h-3.5 w-3.5" />
								</Button>
							)}
							<Button type="submit" size="sm" variant="outline" className="h-8 text-gray-600">
								Search
							</Button>
						</form>

						{/* Rows per page */}
						<select
							value={pageSize}
							onChange={handlePageSizeChange}
							className="h-8 rounded-md border border-gray-200 text-sm px-2 text-gray-600 focus:outline-none focus:ring-1 focus:ring-intellend-green"
						>
							{PAGE_SIZE_OPTIONS.map((n) => (
								<option key={n} value={n}>{n} / page</option>
							))}
						</select>

						<Button
							variant="outline"
							size="sm"
							onClick={() => fetchEntries(page, pageSize, search)}
							disabled={loading}
							className="gap-2 text-gray-600 h-8"
						>
							<RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
							Refresh
						</Button>
					</div>
				</div>

				{loading ? (
					<div className="text-sm text-gray-400 py-6 text-center">Loading…</div>
				) : entries.length === 0 ? (
					<div className="text-sm text-gray-400 py-6 text-center">
						{search ? "No entries match your search." : "No pre-approved entries yet."}
					</div>
				) : (
					<>
						<div className="divide-y divide-gray-100">
							{/* Header */}
							<div className="grid grid-cols-[1fr_1fr_2fr_auto_auto] gap-2 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
								<span>Restaurant ID</span>
								<span>Phone</span>
								<span>Restaurant Name</span>
								<span>Added</span>
								<span />
							</div>

							{entries.map((entry) =>
								editingId === entry.id ? (
									/* ── Edit row ── */
									<div
										key={entry.id}
										className="grid grid-cols-[1fr_1fr_2fr_auto_auto] gap-2 items-center py-2 px-1"
									>
										<Input
											value={editState.restaurant_id}
											onChange={(e) =>
												setEditState((s) => ({ ...s, restaurant_id: e.target.value }))
											}
											placeholder="— none —"
											className="font-mono text-sm h-8"
											type="number"
										/>
										<Input
											value={editState.phone_number}
											onChange={(e) =>
												setEditState((s) => ({ ...s, phone_number: e.target.value }))
											}
											className="font-mono text-sm h-8"
											maxLength={10}
										/>
										<Input
											value={editState.restaurant_name}
											onChange={(e) =>
												setEditState((s) => ({ ...s, restaurant_name: e.target.value }))
											}
											placeholder="— none —"
											className="text-sm h-8"
										/>
										<span />
										<div className="flex gap-1">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => handleSaveEdit(entry.id)}
												disabled={saving}
												className="text-intellend-green hover:text-intellend-green/80 hover:bg-green-50 h-8 w-8"
											>
												<Check className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={cancelEdit}
												disabled={saving}
												className="text-gray-400 hover:text-gray-600 h-8 w-8"
											>
												<X className="h-4 w-4" />
											</Button>
										</div>
									</div>
								) : (
									/* ── Display row ── */
									<div
										key={entry.id}
										className="grid grid-cols-[1fr_1fr_2fr_auto_auto] gap-2 items-center py-3 px-1"
									>
										<p className="font-mono text-gray-600 text-sm">
											{entry.restaurant_id ?? <span className="text-gray-300">—</span>}
										</p>
										<p className="font-mono font-semibold text-gray-800">{entry.phone_number}</p>
										<p className="text-gray-600 text-sm truncate">
											{entry.restaurant_name ?? <span className="text-gray-300">—</span>}
										</p>
										<p className="text-xs text-gray-400 whitespace-nowrap">
											{new Date(entry.created_at).toLocaleDateString("en-IN", {
												day: "2-digit",
												month: "short",
												year: "numeric",
											})}
										</p>
										<div className="flex gap-1">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => startEdit(entry)}
												disabled={deletingId === entry.id}
												className="text-gray-400 hover:text-intellend-green hover:bg-green-50 h-8 w-8"
											>
												<Pencil className="h-3.5 w-3.5" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => handleDelete(entry)}
												disabled={deletingId === entry.id}
												className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</div>
								),
							)}
						</div>

						{/* ── Pagination controls ─────────────────────────────────────── */}
						{totalPages > 1 && (
							<div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
								{/* Row range info */}
								<p className="text-xs text-gray-400">
									Showing {startRow}–{endRow} of {total}
								</p>

								{/* Page navigation */}
								<div className="flex items-center gap-1">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(1)}
										disabled={page === 1 || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="First page"
									>
										<ChevronsLeft className="h-4 w-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(page - 1)}
										disabled={page === 1 || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="Previous page"
									>
										<ChevronLeft className="h-4 w-4" />
									</Button>

									{/* Page number pills */}
									{Array.from({ length: totalPages }, (_, i) => i + 1)
										.filter((p) => {
											// Show first, last, current ±1, and ellipsis placeholders
											return (
												p === 1 ||
												p === totalPages ||
												Math.abs(p - page) <= 1
											);
										})
										.reduce<(number | "…")[]>((acc, p, idx, arr) => {
											if (idx > 0 && typeof arr[idx - 1] === "number" && (p as number) - (arr[idx - 1] as number) > 1) {
												acc.push("…");
											}
											acc.push(p);
											return acc;
										}, [])
										.map((item, idx) =>
											item === "…" ? (
												<span key={`ellipsis-${idx}`} className="px-1 text-gray-400 text-xs select-none">…</span>
											) : (
												<Button
													key={item}
													variant={item === page ? "default" : "ghost"}
													size="sm"
													onClick={() => goToPage(item as number)}
													disabled={loading}
													className={`h-7 w-7 text-xs p-0 ${
														item === page
															? "bg-intellend-green hover:bg-intellend-green/90 text-white"
															: "text-gray-600"
													}`}
												>
													{item}
												</Button>
											),
										)}

									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(page + 1)}
										disabled={page === totalPages || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="Next page"
									>
										<ChevronRight className="h-4 w-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => goToPage(totalPages)}
										disabled={page === totalPages || loading}
										className="h-7 w-7 text-gray-500 disabled:opacity-30"
										title="Last page"
									>
										<ChevronsRight className="h-4 w-4" />
									</Button>
								</div>

								{/* Jump to page */}
								<form
									onSubmit={(e) => {
										e.preventDefault();
										const val = parseInt((e.currentTarget.elements.namedItem("jumpPage") as HTMLInputElement).value, 10);
										if (!isNaN(val)) goToPage(val);
									}}
									className="flex items-center gap-1.5"
								>
									<span className="text-xs text-gray-400">Go to</span>
									<Input
										name="jumpPage"
										type="number"
										min={1}
										max={totalPages}
										defaultValue=""
										className="h-7 w-14 text-xs text-center font-mono"
										placeholder={String(page)}
									/>
									<Button type="submit" size="sm" variant="outline" className="h-7 text-xs px-2 text-gray-600">
										Go
									</Button>
								</form>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default ManagePreApproved;
